import { FC, useMemo, useState } from 'react';

import { Box } from '@mui/material';
import { Formik } from 'formik';

import NPIAutoComplete from 'src/components/FormikComponents/NPIAutoComplete/NPIAutoComplete';
import GeoMapView from 'src/components/GeoMapView/GeoMapView';
import ReusableGrid from 'src/components/ReusableGrid';
import Contact from 'src/components/SubNavigation/components/Contact';
import useAddressCoordinates from 'src/hooks/useAddressCoordinates';

interface SiteContact {
    email: string;
    lastName: string;
    position: string;
    firstName: string;
    mobileNumber: string;
    telephoneNumber: string;
}

interface BankingDetails {
    serviceChargeCalculatedAt: string;
}

interface TradingAddress {
    county: string;
    country: string;
    eircode: string;
    addressLine1: string;
    addressLine2: string;
}

interface Outlet {
    name: string;
    siteContact: SiteContact;
    bankingDetails: BankingDetails;
    tradingAddress: TradingAddress;
}

interface OutletSectionProps {
    height?: number | string;
    outlets: Outlet[];
    useFullWidth?: boolean;
    useEdit?: boolean;
    hideBorderBottom?: boolean;
}

const OutletSection: FC<OutletSectionProps> = ({
    height,
    outlets,
    useFullWidth,
    useEdit,
    hideBorderBottom = false,
}: OutletSectionProps) => {
    const [selectedOutlet, setSelectedOutlet] = useState<Outlet>(outlets?.[0]);

    const outletOptions = useMemo(() => {
        return (
            outlets?.map((outlet) => {
                return {
                    id: outlet.name,
                    label: outlet.name,
                    value: outlet.name,
                };
            }) || []
        );
    }, [outlets]);

    const { data: addressCoordinates = {} } = useAddressCoordinates({
        eircode: selectedOutlet?.tradingAddress?.eircode,
        country: selectedOutlet?.tradingAddress?.country,
        enabled: !!selectedOutlet?.tradingAddress?.eircode,
    });

    const mapKey = useMemo(() => {
        return `${selectedOutlet?.name}-${addressCoordinates.latitude || ''}-${addressCoordinates.longitude || ''}`;
    }, [selectedOutlet?.name, addressCoordinates.latitude, addressCoordinates.longitude]);

    const selectedOutletValue = useMemo(() => {
        if (!selectedOutlet) return null;
        return {
            id: selectedOutlet.name,
            label: selectedOutlet.name,
            value: selectedOutlet.name,
        };
    }, [selectedOutlet]);

    const contactInfo = useMemo(() => {
        if (!selectedOutlet) return null;

        const { name, siteContact, tradingAddress } = selectedOutlet;

        if (!siteContact || !tradingAddress) return null;

        const { addressLine2, addressLine1, eircode, county, country } = tradingAddress;

        return {
            outletName: name,
            firstName: siteContact.firstName,
            lastName: siteContact.lastName,
            email: siteContact.email,
            mobilePhoneNumber: siteContact.mobileNumber,
            country: `${addressLine1} ${addressLine2} ${eircode} ${county} ${country}`,
            isBillingContact: true,
        };
    }, [selectedOutlet]);

    return (
        <Box flex={`0 0 ${useFullWidth ? '100%' : '73%'}`} height={height}>
            <Formik initialValues={outlets} onSubmit={() => {}}>
                {() => {
                    return (
                        <ReusableGrid
                            title="Site Information"
                            xs={12}
                            handleEdit={() => {
                                // Placeholder for edit functionality in future development
                            }}
                            showEditIcon={useEdit}
                            enableContainer
                        >
                            <Box
                                display="flex"
                                gap={useFullWidth ? 15 : 2}
                                height="100%"
                                px={3}
                                pb={3}
                            >
                                <Box
                                    flex={`0 0 ${useFullWidth ? '40%' : '50%'}`}
                                    sx={{ '& .MuiMenuList-root': { display: 'none' } }}
                                >
                                    {outlets?.length > 1 && (
                                        <NPIAutoComplete
                                            header="Outlet(s)"
                                            name="outlets?.[0].name"
                                            options={outletOptions}
                                            placeholder="Please select an outlet"
                                            value={selectedOutletValue}
                                            onChange={(value) => {
                                                const selected = outlets.find((outlet) => {
                                                    return outlet.name === value.value;
                                                });
                                                if (selected) {
                                                    setSelectedOutlet(selected);
                                                }
                                            }}
                                        />
                                    )}
                                    {contactInfo && (
                                        <Contact
                                            contactInformation={contactInfo}
                                            hideBorderBottom={hideBorderBottom}
                                        />
                                    )}
                                </Box>

                                <GeoMapView
                                    key={mapKey}
                                    longitude={addressCoordinates.longitude || 0}
                                    latitude={addressCoordinates.latitude || 0}
                                    address={addressCoordinates.name || ''}
                                />
                            </Box>
                        </ReusableGrid>
                    );
                }}
            </Formik>
        </Box>
    );
};

export default OutletSection;
